/* eslint-disable react/jsx-indent */
import { AnnotatedGraph, HighlightedSentence } from '@litlingo/react';
import React, { useState } from 'react';
import { getUser } from 'selectors/auth';
import {
  getCampaignsRulesFromCommunication,
  getSelectedCampaign,
  getSelectedRule,
} from 'selectors/envelopeReview';
import { getCommsFullMessageHighlighted, getEvent, getEventDocCommLoading } from 'selectors/events';
import { getPermissionsPolicy } from 'selectors/permissions';
import { useSelector } from 'store';
import type { UUID } from 'types';
import { getFieldAuthorization } from 'utils/permissions';
import EnvelopeSkeleton from './EnvelopeSkeleton';
import HighlightedParagraph from './HighlightedParagraph';

type ComponentProps = {
  eventId: UUID;
  commUuid: UUID | null;
  onlySubject?: boolean;
  borderSeparation?: string;
  highRiskOnly?: boolean;
  underlineOnly?: boolean;
  hideHighlight?: boolean;
  handleSentenceClick?: (line: AnnotatedGraph, nodes: number[]) => void;
};

export type HoverData = {
  line: number;
  hoverIndex: number;
  firstNode: number;
  lastNode: number;
  yPos: number;
};

const WrapperHighlightedSentenceComponent: React.FC<ComponentProps> = ({
  eventId,
  commUuid,
  onlySubject,
  borderSeparation,
  highRiskOnly = false,
  underlineOnly = false,
  hideHighlight = false,
  handleSentenceClick,
}) => {
  const user = useSelector(getUser);
  const policy = useSelector(getPermissionsPolicy);
  const entityFieldLevel = getFieldAuthorization('communications.entity', user.roles, policy);

  const campaignsRules = useSelector((state) =>
    getCampaignsRulesFromCommunication(state, commUuid || '')
  );
  const selectedCampaign = useSelector(getSelectedCampaign);

  const [hoverData, setHoverData] = useState<HoverData>({
    line: -1,
    hoverIndex: -1,
    firstNode: -1,
    lastNode: -1,
    yPos: -1,
  });

  const res = useSelector((state) =>
    getCommsFullMessageHighlighted(
      state,
      commUuid || '',
      highRiskOnly.toString(),
      underlineOnly.toString()
    )
  );

  const loading = useSelector(getEventDocCommLoading);

  const event = useSelector((state) => getEvent(state, eventId));
  const selectedRule = useSelector(getSelectedRule);

  if (loading || !res) return <EnvelopeSkeleton includeMessageInfo={false} />;

  if (onlySubject) {
    return (
      <div>
        {res
          .filter((line) => line.type === 'subject')
          .map((line, idx) => (
            <div key={line.index} className={idx !== 0 ? 'mt-2' : ''}>
              <HighlightedSentence
                nodes={line.nodes}
                useHover={user.roles.includes('super-admin')}
                protectedFields={{
                  entity: entityFieldLevel,
                }}
                portal="tooltip-portal"
                ruleId={event?.rule_uuid}
                hideHighlight={hideHighlight}
              />
            </div>
          ))}
      </div>
    );
  }

  const renderContent = (): JSX.Element[] =>
    res
      .filter((line) => line.type === 'line')
      .map((line) => {
        let ruleUuid = '';
        let campaignUuid = '';

        if (line.nodes.some((node) => node.annotations)) {
          const nodes = line.nodes.filter((node) => node.annotations);
          nodes.forEach((node) =>
            node.annotations?.forEach((ann) => {
              if (
                selectedRule?.rule_group_uuid &&
                ann?.rule_uuid === selectedRule?.rule_group_uuid
              ) {
                ruleUuid = selectedRule?.rule_group_uuid;
              }

              if (campaignsRules && selectedCampaign) {
                const rulesCampaign = campaignsRules[selectedCampaign];

                if (
                  rulesCampaign &&
                  rulesCampaign.rules.some((r) => r.rule_group_uuid === ann?.rule_uuid)
                ) {
                  campaignUuid = selectedCampaign;
                }
              }
            })
          );
        }

        return (
          <HighlightedParagraph
            key={line.index}
            index={line.index}
            line={line}
            ruleId={ruleUuid}
            campaignId={campaignUuid}
            borderSeparation={borderSeparation}
            handleSentenceClick={handleSentenceClick}
            hideHighlight={hideHighlight}
            hoverData={hoverData}
            setHoverData={setHoverData}
            communicationId={commUuid || ''}
          />
        );
      });

  return <>{renderContent()}</>;
};

export default WrapperHighlightedSentenceComponent;
